import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem } from 'polished';
import { capitalizeFirstLetter, getUrl } from '../../../utilities';
import {
  setCategoryChanged,
  setCurrentCategoryGroup,
  setCurrentCategoryGroupId,
} from '../../../containers/App/actions';
import {
  ArrowRightIcon,
  BooksIcon,
  BuildingIcon,
  DiscountIcon,
  DrawingIcon,
  EditIcon,
  GalleryIcon,
  GiftIcon,
  HouseIcon,
  HowToLearnIcon,
  MagicStarIcon,
  OfficeIcon,
  SchoolBagIcon,
  ToysIcon,
} from './icons';
import CategoryFlyout from './CategoryFlyout';

type IconComponent = React.FC<{ className?: string }>;

const ICON_BY_UNIQID: { [uniqid: string]: IconComponent } = {
  KAT_AKCIA_SEZONA: DiscountIcon,
  KAT_SKOLA: SchoolBagIcon,
  KAT_TVORIVOST: GalleryIcon,
  KAT_PISANIE: EditIcon,
  KAT_KANCELARIA: OfficeIcon,
  KAT_DOMACNOST: HouseIcon,
  KAT_PARTY: MagicStarIcon,
  KAT_HRYAHRACKY: ToysIcon,
  KAT_UCENIEAKNIHY: BooksIcon,
  KAT_KRESLENIE: DrawingIcon,
  KAT_AKOSAUCIT: HowToLearnIcon,
  FURNITURE_SCHOOL: BuildingIcon,
};
const DEFAULT_ICON: IconComponent = GiftIcon;

const FURNITURE_SCHOOL_UNIQID = 'FURNITURE_SCHOOL';

interface CategoryGroup {
  id: number;
  name: string;
  uniqid?: string;
  uniqid_url?: string | null;
  categories?: Array<{
    category_id?: number;
    category_name?: string;
    url?: string | null;
  }>;
  subgroups?: Array<{
    id?: number;
    name?: string;
    uniqid_url?: string | null;
    categories?: Array<{
      category_id?: number;
      category_name?: string;
      url?: string | null;
    }>;
  }>;
}

interface SideMenuProps {
  categoriesGroup: CategoryGroup[];
  dispatch: any;
  currentPath?: string;
  activeGroupId?: number;
  activeCategoryId?: number;
  className?: string;
  furniture?: boolean;
}

function itemTreeContainsCategory(item: any, categoryId?: number): boolean {
  if (item == null || categoryId == null) {
    return false;
  }
  if (item.category_id === categoryId) {
    return true;
  }
  const children = item.children || item.categories || [];
  return children.some((child: any) =>
    itemTreeContainsCategory(child, categoryId),
  );
}

function groupChildItems(group?: CategoryGroup): any[] {
  if (!group) {
    return [];
  }
  const virtualSubgroups = (group.subgroups || []).map((sg: any) => ({
    category_id: `subgroup_${sg.id}`,
    category_name: sg.name || '',
    url: sg.uniqid_url || sg.categories?.[0]?.url || null,
    image: sg.image_name || sg.content?.image || null,
    children: sg.categories || [],
  }));
  const categories = group.categories || [];
  if (group.uniqid === 'KAT_AKCIA_SEZONA') {
    const akcia = categories.find((c: any) => c.category_id === 1409);
    const novinky = categories.find((c: any) => c.category_id === 1545);
    const rest = categories.filter(
      (c: any) => c.category_id !== 1409 && c.category_id !== 1545,
    );
    return [
      ...(akcia ? [akcia] : []),
      ...(novinky ? [novinky] : []),
      ...virtualSubgroups,
      ...rest,
    ];
  }
  return [...virtualSubgroups, ...categories];
}

function groupUrl(group: CategoryGroup): string {
  const raw =
    group.uniqid_url ||
    group.categories?.[0]?.url ||
    group.subgroups?.[0]?.categories?.[0]?.url ||
    '';
  return raw ? getUrl(raw) : '/';
}

function SideMenu({
  categoriesGroup = [],
  dispatch,
  currentPath = '',
  activeGroupId,
  activeCategoryId,
  className,
  furniture = false,
}: SideMenuProps) {
  const [suppressed, setSuppressed] = React.useState(false);
  const navRef = React.useRef<HTMLElement | null>(null);
  const [menuBottom, setMenuBottom] = React.useState<number | null>(null);
  const onEnterRow = () => {
    setSuppressed(false);
    setMenuBottom(navRef.current?.getBoundingClientRect().bottom ?? null);
  };

  const onClickGroup = (group: CategoryGroup) => {
    if (dispatch) {
      dispatch(setCategoryChanged(true));
      dispatch(setCurrentCategoryGroupId(group.id));
      dispatch(setCurrentCategoryGroup(group));
    }
  };

  if (furniture) {
    const furnitureGroup = categoriesGroup.find(
      (g) => g.uniqid === FURNITURE_SCHOOL_UNIQID,
    );
    const items = groupChildItems(furnitureGroup);
    const onClickFurniture = () => {
      if (dispatch && furnitureGroup) {
        dispatch(setCategoryChanged(true));
        dispatch(setCurrentCategoryGroupId(furnitureGroup.id));
        dispatch(setCurrentCategoryGroup(furnitureGroup));
      }
    };
    return (
      <Nav
        ref={navRef}
        className={`${className || ''}${suppressed ? ' is-suppressed' : ''}`}
        onMouseLeave={() => setSuppressed(false)}
      >
        {items.map((item, i) => {
          const to = item.url ? getUrl(item.url) : '/';
          const isActive =
            activeCategoryId != null
              ? itemTreeContainsCategory(item, activeCategoryId)
              : to !== '/' && currentPath.startsWith(to);
          const children = (item.children || []).filter(Boolean);
          return (
            <ItemWrap key={item.category_id ?? i} onMouseEnter={onEnterRow}>
              <Item
                to={to}
                className={isActive ? 'active' : ''}
                onClick={() => {
                  onClickFurniture();
                  setSuppressed(true);
                }}
              >
                <BuildingIcon className="cat-icon" />
                <Label>{capitalizeFirstLetter(item.category_name || '')}</Label>
                {children.length ? (
                  <ArrowRightIcon className="cat-arrow" />
                ) : null}
              </Item>
              {children.length ? (
                <Flyout className="cat-flyout">
                  <CategoryFlyout
                    items={children}
                    onNavigate={() => {
                      onClickFurniture();
                      setSuppressed(true);
                    }}
                    onDismiss={() => setSuppressed(true)}
                    dismissBelow={menuBottom}
                  />
                </Flyout>
              ) : null}
            </ItemWrap>
          );
        })}
      </Nav>
    );
  }

  const orderedGroups = categoriesGroup.filter(
    (group) => group.uniqid !== 'FURNITURE_SCHOOL',
  );

  return (
    <Nav
      ref={navRef}
      className={`${className || ''}${suppressed ? ' is-suppressed' : ''}`}
      onMouseLeave={() => setSuppressed(false)}
    >
      {orderedGroups.map((group) => {
        const Icon =
          (group.uniqid && ICON_BY_UNIQID[group.uniqid]) || DEFAULT_ICON;
        const to = groupUrl(group);
        const isActive = activeGroupId != null && group.id === activeGroupId;
        const children = groupChildItems(group);
        return (
          <ItemWrap key={group.id} onMouseEnter={onEnterRow}>
            <Item
              to={to}
              className={isActive ? 'active' : ''}
              onClick={() => {
                onClickGroup(group);
                setSuppressed(true);
              }}
            >
              <Icon className="cat-icon" />
              <Label>{capitalizeFirstLetter(group.name)}</Label>
              {children.length ? (
                <ArrowRightIcon className="cat-arrow" />
              ) : null}
            </Item>
            {children.length ? (
              <Flyout className="cat-flyout">
                <CategoryFlyout
                  items={children}
                  onNavigate={() => {
                    onClickGroup(group);
                    setSuppressed(true);
                  }}
                  onDismiss={() => setSuppressed(true)}
                  dismissBelow={menuBottom}
                />
              </Flyout>
            ) : null}
          </ItemWrap>
        );
      })}
    </Nav>
  );
}

const NEAR_BLACK = '#171717';
const HIGHLIGHT_YELLOW = '#fff5b7';

const Nav = styled.nav`
  position: relative;
  /* Fill the left panel column (CategoriesCol = figma 272px) instead of a
     fixed 248px, so the menu spans the full panel width. */
  width: 100%;
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid #e0e0e0;
  border-radius: 0 0 ${rem(8)} ${rem(8)};
  padding: ${rem(8)} 0;

  /* After clicking a tile, force the flyout shut even though the cursor is still
     over it (overrides the :hover rule until the cursor leaves / re-enters). */
  &.is-suppressed .cat-flyout {
    display: none !important;
  }

  /* While a flyout is open (a row with subcategories — or its panel — is
     hovered, and not click-suppressed), dim the rest of the page with a giant
     shadow. The shadow paints below this menu's own background and below the
     flyout panel, so exactly those two stay bright. It covers the header too:
     the column (CategoriesCol, grid.tsx) lifts itself above the fixed header
     with a matching :has() rule while the flyout is open. */
  &:not(.is-suppressed):has(> div:hover > .cat-flyout) {
    box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.4);
    /* The sidebar filters below the menu contain position:relative bits (custom
       checkboxes, slider handles) that share the z-auto layer with this nav and
       come later in the DOM — without a lift they'd paint above the shadow and
       stay bright. */
    z-index: 1;
  }
`;

const ItemWrap = styled.div`
  /* Reveal the flyout while hovering the row OR the flyout itself — the panel is
     a descendant, so hovering it still counts as hovering this wrapper, which
     also keeps the row highlighted (no flicker when crossing the small gap). */
  &:hover > .cat-flyout {
    display: block;
  }
  &:hover > a {
    background: ${HIGHLIGHT_YELLOW};
  }
  /* While the cursor is somewhere over the menu, the yellow highlight follows
     the hovered row instead: the selected row gives it up until the cursor
     leaves the menu again. */
  nav:hover &:not(:hover) > a.active {
    background: transparent;
  }
`;

const Item = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(12)};
  color: ${NEAR_BLACK};
  font-size: ${rem(15)};
  line-height: 1.6;
  text-decoration: none;

  .cat-icon {
    width: ${rem(24)};
    height: ${rem(24)};
    flex: 0 0 auto;
    color: ${({ theme }) => theme.color.primary};
  }

  .cat-arrow {
    width: ${rem(16)};
    height: ${rem(16)};
    flex: 0 0 auto;
    color: ${NEAR_BLACK};
  }

  &:hover {
    background: ${HIGHLIGHT_YELLOW};
    color: ${NEAR_BLACK};
    text-decoration: none;
  }

  &.active,
  &.active:hover {
    background: ${HIGHLIGHT_YELLOW};
    color: ${NEAR_BLACK};
    font-weight: 600;
  }
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Flyout = styled.div`
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  z-index: 40;
`;

export default SideMenu;
